import { tokenize } from './tokenize';
import { parse } from './parse';
import { emit } from './emit';
import { validate } from './validate';
import {
  BooleanQueryError,
  BooleanQueryErrorCode,
  Node,
  and,
  not,
  or,
  term,
} from './types';

export { BooleanQueryError };
export type { BooleanQueryErrorCode };

export interface QueryFields {
  obligatory: string;
  optional: string;
  banned: string;
}

export interface TranslationResult {
  query: string;
  error?: BooleanQueryError;
}

const buildCommaSeparatedTerms = (value: string): Node[] =>
  value
    .split(',')
    .map(part => part.trim())
    .filter(part => part !== '')
    .map(term);

const buildContributions = ({
  obligatory,
  optional,
  banned,
}: QueryFields): Node[] => {
  const contributions: Node[] = [];

  if (obligatory.trim() !== '') {
    contributions.push(parse(tokenize(obligatory)));
  }

  const optionalTerms = buildCommaSeparatedTerms(optional);
  if (optionalTerms.length === 1) {
    contributions.push(optionalTerms[0]);
  } else if (optionalTerms.length > 1) {
    contributions.push(or(optionalTerms));
  }

  buildCommaSeparatedTerms(banned).forEach(bannedTerm => {
    contributions.push(not(bannedTerm));
  });

  return contributions;
};

export const translate = (fields: QueryFields): TranslationResult => {
  let root: Node;

  try {
    const contributions = buildContributions(fields);

    if (contributions.length === 0) {
      return { query: '' };
    }

    root = contributions.length === 1 ? contributions[0] : and(contributions);
  } catch (error) {
    if (error instanceof BooleanQueryError) {
      return { query: fields.obligatory.trim(), error };
    }
    throw error;
  }

  try {
    validate(root);
  } catch (error) {
    if (error instanceof BooleanQueryError) {
      return { query: emit(root), error };
    }
    throw error;
  }

  return { query: emit(root) };
};
